import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { useTranslation } from 'react-i18next'

// local imports
import { IFilteredCoiffeurList, IFilteredCoiffeurSalonList, IGetCoiffeurTimeTableList } from 'types/CoiffeurType'
import DateUtil from 'utils/DateUtil'
import useSaveCoiffeurReservation from '../hooks/use-save-coiffeur-reservation'
import {
  ContentArea,
  Header,
  ImagePreview,
  InfoList,
  InfoListItem,
  ReservationSummary,
  SidebarActionArea,
  SidebarCancelButton,
  SidebarConfirmButton,
  SidebarContainer,
} from './styled'

/* assets */
import calendarr from 'assets/icons/calendar-gray.svg'
import time from 'assets/icons/time.svg'
import location from 'assets/icons/location-marker-gray.svg'
import building from 'assets/icons/bina-gray.svg'
import coiffeur from 'assets/icons/coiffeur-search.svg'
import branch from 'assets/icons/list-branch.svg'
import resIcon from 'assets/icons/coiffeur-res.svg'
import FavoriteIconButton from 'components/ui/FavoriteIconButton'
import CoiffeurService from 'service/CoiffeurService'
import AuthUtil from 'utils/AuthUtil'

interface ScheduleSidebarProps {
  openModal: () => void
  closeAfterSave: () => void
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  coiffeurInfo: IFilteredCoiffeurList
  subServiceId: number
  reservationInfo: IGetCoiffeurTimeTableList
  salon: IFilteredCoiffeurSalonList
}

export default function CoiffeurScheduleSidebar({
  salon,
  coiffeurInfo,
  subServiceId,
  reservationInfo,
  visible,
  setVisible,
  openModal,
  closeAfterSave,
}: ScheduleSidebarProps) {
  const { t } = useTranslation()
  const date = DateUtil.dateFormat(new Date(reservationInfo.reservationEndHour))
  const appointmentTime = `${reservationInfo.reservationStartHourStr} - ${reservationInfo.reservationEndHourStr}`
  const employee = AuthUtil.getEmployeeSummary()

  // Coiffeur - Salon information desctructuring
  const {
    id,
    isFavorite,
    name: salonName,
    floor: { name: floorName, locationName: locationName, districtName: districtName, cityName: cityName, countryName: countryName, blockName: blockName },
  } = salon || {}
  const { name, surname, coiffeurSkills, employeeId } = coiffeurInfo || {}
  const reservationInfo_ = { ...reservationInfo, employeeId, subserviceId: subServiceId }
  const coiffeurFullName = `${name} ${surname}`
  const mainServiceName = coiffeurSkills.find((cs) => cs.coiffeurSubServiceId === subServiceId)?.coiffeurMainServiceName
  const salonLocationName = `${salonName} - ${locationName}, ${districtName}, ${cityName}, ${countryName} `
  /* Handle Save Coiffeur Reservation | Custom Hook */
  const { loading, saveCoiffeurReservation } = useSaveCoiffeurReservation(reservationInfo_, closeAfterSave)

  const closeSidebar = () => {
    setVisible(false)
    openModal()
  }

  const reservationInfoItems = useMemo(
    () => [
      {
        id: 'date-info',
        icon: calendarr,
        title: t('coiffeur-res-modal.reservation-date'),
        value: date,
      },
      {
        id: 'time-info',
        icon: time,
        title: t('coiffeur-res-modal.reservation-time'),
        value: appointmentTime,
      },
      {
        id: 'location-info',
        icon: location,
        title: t('coiffeur-res-modal.coiffeur-location'),
        value: salonLocationName,
      },
      {
        id: 'block-floor-info',
        icon: building,
        title: t('coiffeur-res-modal.block-floor'),
        value: `${blockName} / ${floorName}`,
      },
      {
        id: 'coiffeur-name',
        icon: coiffeur,
        title: t('coiffeur-res-modal.coiffeur-name'),
        value: coiffeurFullName,
      },
      {
        id: 'coiffeur-skilss',
        icon: branch,
        title: t('coiffeur-res-modal.coiffeur-main-service'),
        value: mainServiceName,
      },
    ],
    [appointmentTime, date, coiffeurFullName, mainServiceName, blockName, salonLocationName, floorName, t]
  )

  /* Favorite Coiffeur */
  const [favorite, setFavorite] = useState(isFavorite)
  const handleAddFavorite = () => {
    CoiffeurService.addCoiffeurFavorite(id).then((r) => setFavorite(true))
  }
  const handleRemoveFavorite = () => {
    CoiffeurService.removeCoiffeurFavorite(id).then((r) => setFavorite(false))
  }

  return (
    <Sidebar
      style={{ width: '500px' }}
      className="of-bordered-sidebar-box"
      visible={visible}
      position="right"
      onHide={closeSidebar}
      dismissable={false}
      closeOnEscape={false}>
      <SidebarContainer>
        <ContentArea>
          <Header className="d-flex align-items-center gap-2">
            {t('coiffeur-res-modal.coiffeur-reservation')}
            {AuthUtil.isAdmin(employee?.authList) && (
              <FavoriteIconButton onSave={handleAddFavorite} onRemove={handleRemoveFavorite} isFavorite={favorite} />
            )}
          </Header>
          <ReservationSummary>{t('coiffeur-res-modal.reservation-summary')}</ReservationSummary>
          <ImagePreview url={resIcon} />
          <InfoList>
            <div className="reservation-subtitle">{t('coiffeur-res-modal.reservation-info')}</div>
            {reservationInfoItems.map((info) => (
              <InfoListItem key={info.id}>
                <img src={info.icon} alt={info.id} />
                <span className="reservation-info-title"> {info.title} : </span>
                <span className="reservation-info-value">{info.value}</span>
              </InfoListItem>
            ))}
          </InfoList>
        </ContentArea>

        {/* Action Buttons */}
        <SidebarActionArea>
          <SidebarCancelButton
            id="backToCoiffeurReservation"
            className="p-button-outlined"
            label={t('coiffeur-res-modal.back-to-modal')}
            onClick={closeSidebar}
          />

          <SidebarConfirmButton
            id="saveCoiffeurReservation"
            loading={loading}
            label={t('buttons.confirm')}
            onClick={saveCoiffeurReservation}
          />
        </SidebarActionArea>
      </SidebarContainer>
    </Sidebar>
  )
}
